import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import Swal from 'sweetalert2';

import {
  TranslationsLibService,
  SessionLibService,
  HelpersLibService,
  SidebarLibService,
  AppCommonsModule,
  SidebarComponent,
} from '@nutricontrol/app360-shared';

import { environment } from '../environments/environment';
import { CheckConnectionLibService } from './services/libraries/check-connection-lib.service';
import { RouteLib } from './services/libraries/route-lib';
import { IonicModule } from '@ionic/angular';
import { MainHeaderComponent } from './commons/components/main-header/main-header.component';
import { App360BillingSharedModule } from '@nutricontrol/app360-billing-shared';
import { PopupCookiesComponent } from './commons/components/popup-cookies/popup-cookies.component';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    AppCommonsModule,
    IonicModule,
    SidebarComponent,
    MainHeaderComponent,
    App360BillingSharedModule,
    PopupCookiesComponent,
  ],
})
export class AppComponent implements OnInit {
  session = inject(SessionLibService);
  translations = inject(TranslationsLibService);
  routeLib = inject(RouteLib);
  private helpers = inject(HelpersLibService);
  private sidebarLib = inject(SidebarLibService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  translationsLoaded = signal(false);
  hasSession = signal(false);
  showSidebar = signal(false);
  showQueryIsLoading = signal(false);
  isMenuOpen = signal(false);

  constructor() {
    const translations = this.translations;
    const checkConnection = inject(CheckConnectionLibService);

    translations.initialize(true);
    this.translations
      .getMyUCROPTranslationsLoadedEmitter()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translationsLoaded) => {
        this.translationsLoaded.set(translationsLoaded);
      });

    checkConnection.initialize();

    environment.language = this.helpers.getUsersLocale('en');

    // Hacks depending on hashes
    this.routeLib.navigationStart();
    this.routeLib
      .getNavigationLifecycleIsRunningEmitter()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((navigationLifecycleIsRunning) => {
        this.showQueryIsLoading.set(navigationLifecycleIsRunning);
      });

    if (this.session.getShowAlert()) {
      Swal.fire({
        toast: true,
        icon: 'warning',
        title: 'Está conectado como: ' + this.session.getSessionEmail(),
        position: 'bottom-left',
        showConfirmButton: false,
        background: '#FFFF00',
        iconColor: '#000000',
        color: '#000000',
        timer: 300,
        width: 'auto',
        didOpen: () => {
          Swal.toggleTimer();
        },
      });
    }
  }

  ngOnInit() {
    this.refreshSessionLayout();

    // Listen to router events to show/hide the sidebar
    this.router.events
      .pipe(
        filter((ev) => ev instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.refreshSessionLayout();
      });

    if (this.session.getAccessibilityMode()) {
      document.body.classList.add('accessibility-mode');
    } else {
      document.body.classList.remove('accessibility-mode');
    }
  }

  menuOpened() {
    this.isMenuOpen.set(true);
  }

  menuClosed() {
    this.isMenuOpen.set(false);
  }

  private refreshSessionLayout() {
    const hasSession = !!this.session.getSessionToken();

    this.hasSession.set(hasSession);
    this.showSidebar.set(hasSession && this.sidebarLib.showSidebar());
  }

  protected readonly environment = environment;
}
